import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCurrentUser, signOut as amplifySignOut, fetchUserAttributes } from 'aws-amplify/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAuthState();
  }, []);

  // Note: Session persistence is handled by AWS Amplify Auth automatically
  // No manual credential storage needed

  const checkAdminStatus = async (userEmail) => {
    try {
      // Dynamically import fetchAuthSession to avoid breaking tests that mock aws-amplify/auth
      // without including fetchAuthSession in their mock
      const authModule = await import('aws-amplify/auth');
      if (typeof authModule.fetchAuthSession === 'function') {
        const session = await authModule.fetchAuthSession();
        const groups = session?.tokens?.accessToken?.payload?.['cognito:groups'] || [];
        if (groups.includes('admin')) {
          return true;
        }
      }
    } catch {
      // Session unavailable or not supported — fall through to env var check
    }

    // Fallback: check VITE_ADMIN_EMAILS env var for dev/testing
    const adminEmails = import.meta?.env?.VITE_ADMIN_EMAILS || '';
    if (adminEmails && userEmail) {
      const list = adminEmails.split(',').map((e) => e.trim().toLowerCase());
      if (list.includes(userEmail.toLowerCase())) {
        return true;
      }
    }

    return false;
  };

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();

      if (currentUser) {
        // Fetch user attributes to get the actual user data
        const userAttributes = await fetchUserAttributes();
        const userEmail = userAttributes.email || '';

        // Transform Amplify Gen 2 user format to our expected format
        const transformedUser = {
          userId: currentUser.userId,
          username: currentUser.username,
          attributes: {
            email: userEmail,
            givenName: userAttributes.given_name || '',
            sub: currentUser.userId
          }
        };

        setUser(transformedUser);
        setIsAuthenticated(true);

        // Check admin role (non-blocking — errors default to non-admin)
        const adminStatus = await checkAdminStatus(userEmail);
        setIsAdmin(adminStatus);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsAdmin(false);
      }
    } catch (error) {
      // User is not authenticated
      console.log('No authenticated user:', error.message);
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await amplifySignOut();
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    } catch (error) {
      console.error('Error signing out:', error);
      // Still clear local state even if sign out fails
      setUser(null);
      setIsAuthenticated(false);
      setIsAdmin(false);
    }
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    signOut,
    checkAuthState, // Expose this for manual refresh if needed
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
